import { useState, useEffect, useRef } from 'react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import SwingTradeCard from '@/components/trading/SwingTradeCard'
import PreviewSwingTradeCard from '@/components/trading/PreviewSwingTradeCard'
import UpgradePrompt from '@/components/common/UpgradePrompt'
import PageBasedPagination from '@/components/ui/PageBasedPagination'
import MinimalDetailToggle from '@/components/ui/MinimalDetailToggle'
import { faChartArea, faSpinner, faSort, faSortUp, faSortDown, faFilter, faSearch, faTimes, faHeart, faHeartRegular } from '@/fontawesome'
import { APIRoutes } from '@/services/api.routes'
import { PAGINATION } from '@/config'
import type {RecommendationSwingtrade, SortDirection, SwingTradeState, PreviewSwingTradeRecommendation, RecommendationFavorite} from '@/models'
import type { GraphPoint } from '@/modelsAPI'
import { rfcDateToTimestamp } from '@/utils/helpers'
import { useUser } from '@/contexts/UserContext'

interface SwingTradesListProps {
  className?: string
  showHistory?: boolean
  hasAccess?: boolean
}

type SwingTradeWithGraph = RecommendationSwingtrade & {
  graphPoints?: GraphPoint[]
  graphLoading?: boolean
}

export default function SwingTradesList({ className = '', showHistory = false, hasAccess = true }: SwingTradesListProps) {
  const { isAdmin } = useUser()
  const [swingTrades, setSwingTrades] = useState<SwingTradeWithGraph[]>([])
  const [allSwingTrades, setAllSwingTrades] = useState<SwingTradeWithGraph[]>([])
  const [previewRecommendations, setPreviewRecommendations] = useState<PreviewSwingTradeRecommendation[]>([])
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState<string | null>(null)
  const [sortBy, setSortBy] = useState<'startTimestamp' | 'averagePrice' | 'lastAdjustement'>('lastAdjustement')
  const [sortDirection, setSortDirection] = useState<SortDirection>('DESC')
  const [priceFilter, setPriceFilter] = useState<{ min?: number; max?: number }>({})
  const [tempPriceFilter, setTempPriceFilter] = useState<{ min?: number; max?: number }>({})
  const [showFilters, setShowFilters] = useState(false)
  const [hasActiveFilters, setHasActiveFilters] = useState(false)
  const [currentPage, setCurrentPage] = useState(1)
  const [totalCount, setTotalCount] = useState(0)
  const [symbolFilter, setSymbolFilter] = useState('')
  const [sectorFilter, setSectorFilter] = useState('')
  const [boostFilter, setBoostFilter] = useState<'all' | 'boost' | 'noboost'>('all')
  const [onlyFavorites, setOnlyFavorites] = useState(false)
  const [expandedCards, setExpandedCards] = useState<Set<string>>(new Set())
  const [detailView, setDetailView] = useState(false)
  const graphCache = useRef<Map<string, GraphPoint[] | undefined>>(new Map())
  const pageSize = PAGINATION.USER_PAGES.SWING_TRADES
  const isRangeInvalid = (tempPriceFilter.min !== undefined && tempPriceFilter.max !== undefined)
    ? tempPriceFilter.min > tempPriceFilter.max
    : false

  useEffect(() => {
    fetchSwingTrades()
    // Fetch preview recommendations if user doesn't have access
    if (!hasAccess) {
      fetchPreviewRecommendations()
    } else {
      setPreviewRecommendations([])
    }
  }, [showHistory, hasAccess])

  useEffect(() => {
    // Reset to page 1 when filters change
    setCurrentPage(1)
  }, [priceFilter, symbolFilter, sectorFilter, boostFilter, onlyFavorites])

  // Apply sorting, filtering and pagination client-side
  useEffect(() => {
    applyClientSideProcessing()
  }, [allSwingTrades, sortBy, sortDirection, priceFilter, symbolFilter, sectorFilter, boostFilter, onlyFavorites, currentPage])

  const applyClientSideProcessing = () => {
    if (allSwingTrades.length === 0) {
      setSwingTrades([])
      return
    }

    let processedTrades = [...allSwingTrades]

    // Apply symbol filter
    if (symbolFilter.trim() !== '') {
      processedTrades = processedTrades.filter(trade =>
        trade.symbolTicker.toLowerCase().includes(symbolFilter.toLowerCase())
      )
    }

    // Apply sector filter
    if (sectorFilter.trim() !== '') {
      processedTrades = processedTrades.filter(trade =>
        trade.symbol?.sector?.toLowerCase() === sectorFilter.toLowerCase()
      )
    }

    // Apply price filter
    if (priceFilter.min !== undefined || priceFilter.max !== undefined) {
      processedTrades = processedTrades.filter(trade => {
        const price = trade.averagePrice
        const minMatch = priceFilter.min === undefined || price >= priceFilter.min!
        const maxMatch = priceFilter.max === undefined || price <= priceFilter.max!
        return minMatch && maxMatch
      })
    }

    // Apply boost filter (admin only)
    if (boostFilter !== 'all') {
      processedTrades = processedTrades.filter(trade =>
        boostFilter === 'boost' ? trade.boost : !trade.boost
      )
    }

    // Apply favorites filter
    if (onlyFavorites) {
      processedTrades = processedTrades.filter(trade => trade.favorite !== null && trade.favorite !== undefined)
    }

    // Set total count after filtering but before pagination
    setTotalCount(processedTrades.length)

    // Apply sorting
    const directionFactor = sortDirection === 'ASC' ? 1 : -1
    processedTrades = processedTrades.sort((a, b) => {
      if (sortBy === 'startTimestamp') {
        const taStr = rfcDateToTimestamp(a.startTimestamp, false)
        const tbStr = rfcDateToTimestamp(b.startTimestamp, false)
        const ta = Date.parse(taStr)
        const tb = Date.parse(tbStr)
        const taNum = isNaN(ta) ? 0 : ta
        const tbNum = isNaN(tb) ? 0 : tb
        return (taNum - tbNum) * directionFactor
      } else if (sortBy === 'lastAdjustement') {
        // Sort by lastAdjustement timestamp
        const aTime = Date.parse(a.lastAdjustement)
        const bTime = Date.parse(b.lastAdjustement)

        // Handle invalid dates
        if (isNaN(aTime) && isNaN(bTime)) {
          return 0
        }
        if (isNaN(aTime)) {
          return 1
        }
        if (isNaN(bTime)) {
          return -1
        }

        return (aTime - bTime) * directionFactor
      }
      // sortBy === 'averagePrice'
      return (a.averagePrice - b.averagePrice) * directionFactor
    })

    // Apply pagination
    const startIndex = (currentPage - 1) * pageSize
    processedTrades = processedTrades.slice(startIndex, startIndex + pageSize)

    // Re-attach already-loaded graphs from cache to avoid reloading charts on re-process
    const withGraphs = processedTrades.map(trade =>
      graphCache.current.has(trade.id)
        ? { ...trade, graphPoints: graphCache.current.get(trade.id), graphLoading: false }
        : trade
    )

    setSwingTrades(withGraphs)

    // Fetch symbol graph data only for trades whose graph isn't cached yet
    withGraphs.forEach(trade => {
      if (trade.symbol?.id && !graphCache.current.has(trade.id)) {
        fetchSymbolGraph(trade)
      }
    })
  }

  const handleFavoriteChange = (id: string, favorite: RecommendationFavorite | null) => {
    setAllSwingTrades(prev => prev.map(t => t.id === id ? { ...t, favorite } : t))
  }

  const fetchSwingTrades = async () => {
    try {
      setLoading(true)
      setError(null)

      const state: SwingTradeState[] = showHistory ? ['FINISHED_PROFIT', 'FINISHED_LOSS'] : ['OPEN']

      const response = await APIRoutes.listSwingTrades({
        limit: PAGINATION.FETCH_ALL_LIMIT,
        state: state
      })

      setAllSwingTrades(response.trades || [])
    } catch (err) {
      console.error('Failed to fetch swing trades:', err)
      setError('Fehler beim Laden der Swing-Trades')
    } finally {
      setLoading(false)
    }
  }

  const fetchPreviewRecommendations = async () => {
    try {
      const state: SwingTradeState[] = showHistory ? ['FINISHED_PROFIT', 'FINISHED_LOSS'] : ['OPEN']

      const response = await APIRoutes.listPreviewSwingTrades({
        limit: PAGINATION.FETCH_ALL_LIMIT,
        state: state
      })
      // Filter out free ones (they're already in normal recommendations)
      const nonFreePreview = (response.recommendations ?? []).filter(r => !r.free)
      setPreviewRecommendations(nonFreePreview)
    } catch (err) {
      console.error('Failed to fetch preview swing trades:', err)
      // Don't set error - preview is optional
    }
  }

  const fetchSymbolGraph = async (trade: RecommendationSwingtrade) => {
    if (!trade.symbol?.id) return

    try {
      setSwingTrades(prev => prev.map(t =>
        t.id === trade.id ? { ...t, graphLoading: true } : t
      ))

      // Use at least 30 days of history for better chart display
      const oneDaysAgo = new Date(Date.now() - 1 * 24 * 60 * 60 * 1000).toISOString()
      const tradeStart = new Date(trade.startTimestamp).toISOString()
      const start = tradeStart < oneDaysAgo ? tradeStart : oneDaysAgo
      const end = trade.endTimestamp ?? new Date().toISOString()

      const response = await APIRoutes.getSymbolGraph(trade.symbol.id, {
        start,
        end,
        points: PAGINATION.CHART.CARD_POINTS
      })

      let points: GraphPoint[] | undefined = response.points
      if (points.length <= 1) points = undefined

      graphCache.current.set(trade.id, points)

      setSwingTrades(prev => prev.map(t =>
        t.id === trade.id
          ? { ...t, graphPoints: points, graphLoading: false }
          : t
      ))
    } catch (err) {
      console.error('Failed to fetch symbol graph:', err)
      setSwingTrades(prev => prev.map(t =>
        t.id === trade.id ? { ...t, graphLoading: false } : t
      ))
    }
  }

  const handleSort = (newSortBy: 'startTimestamp' | 'averagePrice' | 'lastAdjustement') => {
    if (sortBy === newSortBy) {
      setSortDirection(sortDirection === 'ASC' ? 'DESC' : 'ASC')
    } else {
      setSortBy(newSortBy)
      setSortDirection(newSortBy === 'averagePrice' ? 'ASC' : 'DESC')
    }
    setCurrentPage(1)
  }

  const handleTempFilterChange = (min?: string, max?: string) => {
    setTempPriceFilter({
      min: min && min !== '' ? parseFloat(min) : undefined,
      max: max && max !== '' ? parseFloat(max) : undefined
    })
  }

  const applyFilters = () => {
    const centsFilter = {
      min: tempPriceFilter.min !== undefined ? Math.round(tempPriceFilter.min * 100) : undefined,
      max: tempPriceFilter.max !== undefined ? Math.round(tempPriceFilter.max * 100) : undefined,
    }
    setPriceFilter(centsFilter)
    updateActiveFilters(centsFilter, symbolFilter, sectorFilter, boostFilter)
  }

  const updateActiveFilters = (priceFilterValue: typeof priceFilter, symbol: string, sector: string, boost: 'all' | 'boost' | 'noboost') => {
    const hasPriceFilter = priceFilterValue.min !== undefined || priceFilterValue.max !== undefined
    const hasSymbolFilter = symbol.trim() !== ''
    const hasSectorFilter = sector.trim() !== ''
    const hasBoostFilter = boost !== 'all'
    setHasActiveFilters(hasPriceFilter || hasSymbolFilter || hasSectorFilter || hasBoostFilter)
  }

  const handleSymbolFilterChange = (value: string) => {
    setSymbolFilter(value)
    updateActiveFilters(priceFilter, value, sectorFilter, boostFilter)
  }

  const handleSectorFilterChange = (value: string) => {
    setSectorFilter(value)
    updateActiveFilters(priceFilter, symbolFilter, value, boostFilter)
  }

  const resetFilters = () => {
    setTempPriceFilter({})
    setPriceFilter({})
    setSymbolFilter('')
    setSectorFilter('')
    setBoostFilter('all')
    setOnlyFavorites(false)
    setHasActiveFilters(false)
  }

  const availableSectors = Array.from(new Set(allSwingTrades
    .map(trade => trade.symbol?.sector)
    .filter((sector): sector is string => sector !== null && sector !== undefined && sector !== '')
  )).sort()

  const getSortIcon = (field: 'startTimestamp' | 'averagePrice' | 'lastAdjustement') => {
    if (sortBy !== field) return faSort
    return sortDirection === 'ASC' ? faSortUp : faSortDown
  }

  if (loading) {
    return (
      <div className={`text-center ${className}`}>
        <FontAwesomeIcon icon={faSpinner} className="w-6 h-6 text-gray-400 animate-spin" />
      </div>
    )
  }

  if (error) {
    return (
      <div className={`max-w-7xl mx-auto ${className}`}>
        <div className="bg-red-50 dark:bg-red-900/20 text-red-600 dark:text-red-400 p-4 rounded-lg">
          {error}
        </div>
      </div>
    )
  }

  if (swingTrades.length === 0) {
    return (
      <div className={`max-w-7xl mx-auto ${className}`}>
        {/* Symbol Search */}
        <div className="mb-6">
          <div className="relative max-w-md">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
            </div>
            <input
              type="text"
              placeholder="Symbol filtern (z.B. AAPL, TSLA)..."
              value={symbolFilter}
              onChange={(e) => handleSymbolFilterChange(e.target.value)}
              className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
            />
            {symbolFilter && (
              <button
                onClick={() => handleSymbolFilterChange('')}
                className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
              >
                <FontAwesomeIcon icon={faTimes} className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
              </button>
            )}
          </div>
        </div>

        <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
          <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
            {showHistory ? 'Historische Swing-Trades' : 'Aktive Swing-Trades'}
          </h3>

          <div className="flex flex-col sm:flex-row gap-2">
            {/* Sort Controls */}
            <div className="flex items-center gap-2">
              <span className="text-sm font-medium text-gray-700 dark:text-gray-300">
                Sortieren nach:
              </span>
              <div className="flex gap-2">
                <button
                onClick={() => handleSort('lastAdjustement')}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                  sortBy === 'lastAdjustement'
                    ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Letzte Aktivität
                <FontAwesomeIcon icon={getSortIcon('lastAdjustement')} className="w-3 h-3" />
              </button>

              <button
                onClick={() => handleSort('startTimestamp')}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                  sortBy === 'startTimestamp'
                    ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Datum
                <FontAwesomeIcon icon={getSortIcon('startTimestamp')} className="w-3 h-3" />
              </button>

              <button
                onClick={() => handleSort('averagePrice')}
                className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                  sortBy === 'averagePrice'
                    ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                    : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
                }`}
              >
                Durchschnittspreis
                <FontAwesomeIcon icon={getSortIcon('averagePrice')} className="w-3 h-3" />
              </button>
              </div>
            </div>

            {/* Favorites Toggle */}
            <button
              onClick={() => { setOnlyFavorites(!onlyFavorites); setCurrentPage(1) }}
              title="Nur Favoriten anzeigen"
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                onlyFavorites
                  ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <FontAwesomeIcon icon={onlyFavorites ? faHeart : faHeartRegular} className="w-3 h-3" />
              Favoriten
            </button>

            {/* Filter Toggle */}
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`flex items-center gap-2 px-3 py-2 text-sm rounded-lg border cursor-pointer transition-colors ${
                showFilters || hasActiveFilters
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <FontAwesomeIcon icon={faFilter} className="w-3 h-3" />
              Filter
              {hasActiveFilters && (
                <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                  {[
                    priceFilter.min !== undefined || priceFilter.max !== undefined,
                    symbolFilter.trim() !== '',
                    sectorFilter.trim() !== '',
                    isAdmin && boostFilter !== 'all'
                  ].filter(Boolean).length}
                </span>
              )}
            </button>
          </div>
        </div>

        {/* Filter Panel */}
        {showFilters && (
          <div className="mb-6 p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
            <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Filter</h4>
            <div className="flex flex-col gap-4">
              {/* Sector Filter */}
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 w-20">Branche:</label>
                <select
                  value={sectorFilter}
                  onChange={(e) => handleSectorFilterChange(e.target.value)}
                  className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="">Alle Branchen</option>
                  {availableSectors.map(sector => (
                    <option key={sector} value={sector}>{sector}</option>
                  ))}
                </select>
              </div>

              {/* Boost Filter - nur für Admins */}
              {isAdmin && (
                <div className="flex items-center gap-2">
                  <label className="text-sm text-gray-700 dark:text-gray-300 w-20 shrink-0">Boost:</label>
                  <select
                    value={boostFilter}
                    onChange={(e) => {
                      const val = e.target.value as 'all' | 'boost' | 'noboost'
                      setBoostFilter(val)
                      updateActiveFilters(priceFilter, symbolFilter, sectorFilter, val)
                    }}
                    className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                  >
                    <option value="all">Alle</option>
                    <option value="boost">Boost</option>
                    <option value="noboost">Kein Boost</option>
                  </select>
                </div>
              )}

              {/* Price Filter */}
              <div>
                <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Durchschnittspreis</h5>
                <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-gray-300">Von:</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Min"
                      value={tempPriceFilter.min ?? ''}
                      className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      onChange={(e) => handleTempFilterChange(e.target.value, tempPriceFilter.max?.toString())}
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <label className="text-sm text-gray-700 dark:text-gray-300">Bis:</label>
                    <input
                      type="number"
                      step="0.01"
                      placeholder="Max"
                      value={tempPriceFilter.max ?? ''}
                      className="w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                      onChange={(e) => handleTempFilterChange(tempPriceFilter.min?.toString(), e.target.value)}
                    />
                  </div>
                </div>
              </div>
              {isRangeInvalid && (
                <div className="text-sm text-red-600 dark:text-red-400">Überprüfen sie ihre eingabe.</div>
              )}
              <div className="flex gap-2 justify-start">
                <button
                  onClick={applyFilters}
                  disabled={isRangeInvalid}
                  className={`px-4 py-2 text-sm rounded-lg cursor-pointer transition-colors ${
                    isRangeInvalid
                      ? 'bg-blue-300 text-white cursor-not-allowed'
                      : 'bg-blue-600 text-white hover:bg-blue-700'
                  }`}
                >
                  Übernehmen
                </button>
                <button
                  onClick={resetFilters}
                  className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer transition-colors dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
                >
                  Zurücksetzen
                </button>
              </div>
            </div>
          </div>
        )}

        <div className="text-center py-12">
          <div className="text-gray-500 dark:text-gray-400">
            <FontAwesomeIcon icon={faChartArea} className="w-16 h-16 mb-4 mx-auto" />
            <h3 className="text-xl font-semibold mb-2">
              {(hasActiveFilters || onlyFavorites)
                ? 'Keine Trades gefunden'
                : showHistory
                  ? 'Keine historischen Trades vorhanden'
                  : !hasAccess
                    ? 'Aktuell keine kostenlosen Trades vorhanden'
                    : 'Aktuell keine Trades vorhanden'}
            </h3>
            <p className="text-sm">
              {(hasActiveFilters || onlyFavorites)
                ? 'Mit den aktuellen Filterkriterien wurden keine Swing-Trades gefunden.'
                : showHistory
                  ? 'Es sind derzeit keine historischen Swing-Trades verfügbar.'
                  : 'Es sind derzeit keine aktiven Swing-Trades verfügbar.'
              }
            </p>
          </div>
        </div>

        {/* Preview Section - auch im Empty State anzeigen */}
        {!hasAccess && !showHistory && previewRecommendations.length > 0 && (
          <div className="mt-8">
            <UpgradePrompt
              previewCount={previewRecommendations.length}
              featureName="Swing-Trades"
              className="mb-6"
            />
            <div className="space-y-6">
              {previewRecommendations.map((preview) => (
                <PreviewSwingTradeCard
                  key={preview.id}
                  preview={preview}
                />
              ))}
            </div>
          </div>
        )}
      </div>
    )
  }

  return (
    <div className={`max-w-7xl mx-auto ${className}`}>
      {/* Symbol Search */}
      <div className="mb-6">
        <div className="relative max-w-md">
          <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
            <FontAwesomeIcon icon={faSearch} className="h-4 w-4 text-gray-400" />
          </div>
          <input
            type="text"
            placeholder="Symbol filtern (z.B. AAPL, TSLA)..."
            value={symbolFilter}
            onChange={(e) => handleSymbolFilterChange(e.target.value)}
            className="block w-full pl-10 pr-10 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white placeholder-gray-500 dark:placeholder-gray-400"
          />
          {symbolFilter && (
            <button
              onClick={() => handleSymbolFilterChange('')}
              className="absolute inset-y-0 right-0 pr-3 flex items-center cursor-pointer"
            >
              <FontAwesomeIcon icon={faTimes} className="h-4 w-4 text-gray-400 hover:text-gray-600 dark:hover:text-gray-300" />
            </button>
          )}
        </div>
      </div>

      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between mb-6 gap-4">
        <h3 className="text-xl font-semibold text-gray-900 dark:text-white">
          {showHistory ? 'Historische Swing-Trades' : 'Aktive Swing-Trades'}
        </h3>

        <div className="flex flex-col sm:flex-row gap-2">
          {/* Sort Controls */}
          <div className="flex items-center gap-2">
            <span className="hidden sm:inline text-sm font-medium text-gray-700 dark:text-gray-300">
              Sortieren nach:
            </span>
            <div className="flex gap-2">
              <button
              onClick={() => handleSort('lastAdjustement')}
              className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
                sortBy === 'lastAdjustement'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <span className="hidden sm:inline">Letzte Aktivität</span>
              <span className="sm:hidden">Aktivität</span>
              <FontAwesomeIcon icon={getSortIcon('lastAdjustement')} className="w-3 h-3" />
            </button>

            <button
              onClick={() => handleSort('startTimestamp')}
              className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
                sortBy === 'startTimestamp'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              Datum
              <FontAwesomeIcon icon={getSortIcon('startTimestamp')} className="w-3 h-3" />
            </button>

            <button
              onClick={() => handleSort('averagePrice')}
              className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
                sortBy === 'averagePrice'
                  ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                  : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
              }`}
            >
              <span className="hidden sm:inline">Durchschnittspreis</span>
              <span className="sm:hidden">Ø-Preis</span>
              <FontAwesomeIcon icon={getSortIcon('averagePrice')} className="w-3 h-3" />
            </button>
            </div>
          </div>

          {/* Favorites Toggle */}
          <button
            onClick={() => { setOnlyFavorites(!onlyFavorites); setCurrentPage(1) }}
            title="Nur Favoriten anzeigen"
            className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
              onlyFavorites
                ? 'bg-red-50 border-red-200 text-red-600 dark:bg-red-900/20 dark:border-red-800 dark:text-red-300'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={onlyFavorites ? faHeart : faHeartRegular} className="w-3 h-3" />
            Favoriten
          </button>

          {/* Filter Toggle */}
          <button
            onClick={() => setShowFilters(!showFilters)}
            className={`flex items-center gap-2 px-2 py-1.5 sm:px-3 sm:py-2 text-xs sm:text-sm rounded-lg border cursor-pointer transition-colors ${
              showFilters || hasActiveFilters
                ? 'bg-blue-50 border-blue-200 text-blue-700 dark:bg-blue-900/20 dark:border-blue-700 dark:text-blue-300'
                : 'bg-white border-gray-200 text-gray-700 hover:bg-gray-50 dark:bg-gray-800 dark:border-gray-600 dark:text-gray-300 dark:hover:bg-gray-700'
            }`}
          >
            <FontAwesomeIcon icon={faFilter} className="w-3 h-3" />
            Filter
            {hasActiveFilters && (
              <span className="ml-1 px-1.5 py-0.5 text-xs bg-blue-600 text-white rounded-full">
                {[
                  priceFilter.min !== undefined || priceFilter.max !== undefined,
                  symbolFilter.trim() !== '',
                  sectorFilter.trim() !== '',
                  isAdmin && boostFilter !== 'all'
                ].filter(Boolean).length}
              </span>
            )}
          </button>
        </div>
      </div>

      {/* Mobile: Minimal/Detail Ansicht Toggle - nur in der History */}
      {showHistory && (
        <div className="lg:hidden mb-4 flex justify-end">
          <MinimalDetailToggle detailView={detailView} onToggle={setDetailView} />
        </div>
      )}

      {/* Filter Panel */}
      {showFilters && (
        <div className="mb-6 p-3 lg:p-4 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-sm font-medium text-gray-900 dark:text-white mb-3">Filter</h4>
          <div className="flex flex-col gap-4">
            {/* Sector Filter */}
            <div className="flex items-center gap-2">
              <label className="text-sm text-gray-700 dark:text-gray-300 w-20 shrink-0">Branche:</label>
              <select
                value={sectorFilter}
                onChange={(e) => handleSectorFilterChange(e.target.value)}
                className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Alle Branchen</option>
                {availableSectors.map(sector => (
                  <option key={sector} value={sector}>{sector}</option>
                ))}
              </select>
            </div>

            {/* Boost Filter - nur für Admins */}
            {isAdmin && (
              <div className="flex items-center gap-2">
                <label className="text-sm text-gray-700 dark:text-gray-300 w-20 shrink-0">Boost:</label>
                <select
                  value={boostFilter}
                  onChange={(e) => {
                    const val = e.target.value as 'all' | 'boost' | 'noboost'
                    setBoostFilter(val)
                    updateActiveFilters(priceFilter, symbolFilter, sectorFilter, val)
                  }}
                  className="flex-1 min-w-0 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
                >
                  <option value="all">Alle</option>
                  <option value="boost">Boost</option>
                  <option value="noboost">Kein Boost</option>
                </select>
              </div>
            )}

            {/* Price Filter */}
            <div>
              <h5 className="text-sm font-medium text-gray-900 dark:text-white mb-2">Durchschnittspreis</h5>
              <div className="flex flex-col sm:flex-row gap-4 items-start sm:items-center">
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Von:</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Min"
                    value={tempPriceFilter.min ?? ''}
                    className="w-full sm:w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    onChange={(e) => handleTempFilterChange(e.target.value, tempPriceFilter.max?.toString())}
                  />
                </div>
                <div className="flex items-center gap-2 w-full sm:w-auto">
                  <label className="text-sm text-gray-700 dark:text-gray-300 whitespace-nowrap">Bis:</label>
                  <input
                    type="number"
                    step="0.01"
                    placeholder="Max"
                    value={tempPriceFilter.max ?? ''}
                    className="w-full sm:w-24 px-3 py-2 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white dark:placeholder-gray-400"
                    onChange={(e) => handleTempFilterChange(tempPriceFilter.min?.toString(), e.target.value)}
                  />
                </div>
              </div>
            </div>
            {isRangeInvalid && (
              <div className="text-sm text-red-600 dark:text-red-400">Überprüfen sie ihre eingabe.</div>
            )}
            <div className="flex gap-2 justify-start">
              <button
                onClick={applyFilters}
                disabled={isRangeInvalid}
                className={`px-4 py-2 text-sm rounded-lg cursor-pointer transition-colors ${
                  isRangeInvalid
                    ? 'bg-blue-300 text-white cursor-not-allowed'
                    : 'bg-blue-600 text-white hover:bg-blue-700'
                }`}
              >
                Übernehmen
              </button>
              <button
                onClick={resetFilters}
                className="px-4 py-2 text-sm bg-gray-200 text-gray-700 rounded-lg hover:bg-gray-300 cursor-pointer transition-colors dark:bg-gray-600 dark:text-gray-300 dark:hover:bg-gray-500"
              >
                Zurücksetzen
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="space-y-6">
        {swingTrades.map((trade) => (
          <SwingTradeCard
            key={trade.id}
            trade={trade}
            onFavoriteChange={(fav) => handleFavoriteChange(trade.id, fav)}
            expanded={expandedCards.has(trade.id)}
            detailView={showHistory && detailView}
            onToggle={() => {
              setExpandedCards(prev => {
                const newSet = new Set(prev)
                if (newSet.has(trade.id)) {
                  newSet.delete(trade.id)
                } else {
                  newSet.add(trade.id)
                }
                return newSet
              })
            }}
          />
        ))}
      </div>

      {/* Preview Section - shown when user lacks access and there are preview items */}
      {!hasAccess && !showHistory && previewRecommendations.length > 0 && (
        <div className="mt-8">
          {/* Upgrade CTA before preview cards */}
          <UpgradePrompt
            previewCount={previewRecommendations.length}
            featureName="Swing-Trades"
            className="mb-6"
          />

          {/* Blurred preview cards */}
          <div className="space-y-6">
            {previewRecommendations.map((preview) => (
              <PreviewSwingTradeCard
                key={preview.id}
                preview={preview}
              />
            ))}
          </div>
        </div>
      )}

      {/* Pagination */}
      {totalCount > pageSize && (
        <div className="mt-6">
          <PageBasedPagination
            currentPage={currentPage}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            loading={loading}
          />
        </div>
      )}
    </div>
  )
}
